import html2canvas from 'html2canvas';

/**
 * On-demand screenshots for the AI help assistant's "eyes".
 *
 * Captures the app DOM to a JPEG data URL sized to fit through the OpenAI
 * Realtime WebRTC data channel (messages are capped around 256KB, so we
 * compress until the payload is comfortably under that).
 *
 * Elements marked data-assistant-capture-ignore="true" (the help drawer, the
 * voice pulse overlay, the highlight ring) are excluded so the assistant sees
 * the app itself, not its own chrome sitting on top of it.
 */

/** Attribute that hides an element (and its children) from assistant screenshots. */
export const CAPTURE_IGNORE_ATTR = 'data-assistant-capture-ignore';

const MAX_WIDTH_PX = 1024;
/** Keep the final base64 payload well under the ~256KB data-channel limit. */
const MAX_DATA_URL_BYTES = 200_000;

export async function captureScreenshot(): Promise<string | null> {
  try {
    const scale = Math.min(1, MAX_WIDTH_PX / window.innerWidth);
    const canvas = await html2canvas(document.body, {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      ignoreElements: (el) => el instanceof HTMLElement && el.getAttribute(CAPTURE_IGNORE_ATTR) === 'true',
      // Capture only the visible viewport, not the full scrollable page
      x: window.scrollX,
      y: window.scrollY,
      width: window.innerWidth,
      height: window.innerHeight,
    });

    // Step quality down until the payload fits the data-channel limit
    for (const quality of [0.7, 0.5, 0.35, 0.2]) {
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      if (dataUrl.length <= MAX_DATA_URL_BYTES) return dataUrl;
    }

    // Still too big (huge monitor) — halve the resolution and take what we get
    const half = document.createElement('canvas');
    half.width = Math.floor(canvas.width / 2);
    half.height = Math.floor(canvas.height / 2);
    half.getContext('2d')?.drawImage(canvas, 0, 0, half.width, half.height);
    const dataUrl = half.toDataURL('image/jpeg', 0.5);
    return dataUrl.length <= MAX_DATA_URL_BYTES ? dataUrl : null;
  } catch (err) {
    console.warn('[assistant] screenshot capture failed:', err);
    return null;
  }
}
